import { ref, computed, watch } from 'vue'
import { useBaseStore } from '@/stores'
import { createPaysoOrder, getGamestore } from '@/api/wallet'
import { Gamestore } from '@/api/wallet/type'
import { getDeviceInfo } from '@/utils'
import webApp from '@twa-dev/sdk'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Log } from '@/api/log'
import dayjs from 'dayjs'
import { showFailToast, showDialog } from 'vant'

// 渠道轮换索引存储key
const CHANNEL_ROTATION_KEY = 'betfugu_channel_rotation'

/**
 * 直接支付组合式API
 * 用于首充和签到页等需要跳过渠道选择的场景
 *
 * @param orderParams 订单参数对象，包含产品信息和来源
 * @returns 返回支付相关的状态和方法
 */
export function useDirectPayment(orderParams: any) {
    const store = useBaseStore()
    const router = useRouter()
    const i18n = useI18n()

    // 响应式状态
    const chooseChl = ref('') // 当前选中的支付渠道
    const showLoading = ref(false) // 支付处理中的加载状态

    // 计算属性
    const product = computed(() => orderParams.item || {})

    const takeEffectChannel = computed(() => {
        const id = product.value?.id
        return id ? store.depositeChannel.filter((item: any) => item.productList.includes(id)) : store.depositeChannel
    })

    const uid = computed(() => {
        return store.userInfo.uid
    })

    /**
     * 获取支付渠道数据
     * 调用API获取所有可用的支付渠道和相关产品信息，并根据用户类型进行过滤
     */
    const getChannel = async () => {
        // 检查用户登录状态
        if (!uid.value || !store.token) {
            return
        }

        try {
            // 调用新的 getGamestore 接口获取支付渠道和产品信息
            const res: Gamestore = await getGamestore(uid.value)
            if (res.code === 200) {
                let channels: any[] = []

                // 优先使用 methods 中的支付方式数据
                if (res.methods && res.methods.length > 0) {
                    // 从 methods 中提取所有 channels
                    const methodChannels: string[] = []
                    res.methods.forEach((method: any) => {
                        if (method.channels && method.channels.length > 0) {
                            method.channels.forEach((ch: any) => {
                                if (!methodChannels.includes(ch.channel)) {
                                    methodChannels.push(ch.channel)
                                }
                            })
                        }
                    })

                    // 根据 method channels 过滤根级别的 channels 数据
                    channels = (res.channels || [])
                        .filter((item: any) => methodChannels.includes(item.channel))
                        .map((item: any) => ({
                            channel: item.channel,
                            icon: item.icon || '', // 如果没有 icon 字段，提供默认值
                            productList: item.productList || [],
                            status: item.status || 0,
                            index: item.index || 0,
                            pkgList: item.pkgList || [], // 如果没有 pkgList 字段，提供默认值
                        }))
                } else {
                    // 如果没有 methods 数据，直接使用 channels 数据（向后兼容）
                    channels = (res.channels || []).map((item: any) => ({
                        channel: item.channel,
                        icon: item.icon || '', // 如果没有 icon 字段，提供默认值
                        productList: item.productList || [],
                        status: item.status || 0,
                        index: item.index || 0,
                        pkgList: item.pkgList || [], // 如果没有 pkgList 字段，提供默认值
                    }))
                }

                // 存储完整的渠道数据到 allChannels
                store.allChannels = channels

                // 存储支付方式数据
                if (res.methods && res.methods.length > 0) {
                    store.paymentMethods = res.methods
                    // 如果有 methods 数据，默认选择第一个方式的渠道
                    const firstMethod: any = res.methods[0]
                    if (firstMethod && firstMethod.channels) {
                        updateChannelsByMethod(firstMethod.name, res.methods)
                    }
                } else {
                    store.paymentMethods = []
                    // 如果没有 methods 数据，直接使用所有渠道（向后兼容）
                    store.depositeChannel = store.userInfo?.isReseller ? channels.filter((item: any) => item.channel !== 'reseller') : channels
                }
            }
        } catch (error) {
            console.error('Failed to get channels:', error)
        }
    }

    /**
     * 根据选中的支付方式更新可用渠道
     * @param selectedMethodName 选中的支付方式名称
     * @param allMethods 所有支付方式数据
     */
    const updateChannelsByMethod = (selectedMethodName: string, allMethods?: any[]) => {
        const methods = allMethods || store.paymentMethods
        const selectedMethod = methods.find((method: any) => method.name === selectedMethodName)

        if (!selectedMethod || !selectedMethod.channels) {
            store.depositeChannel = []
            return
        }

        // 获取选中方式下的 channel 名称
        const methodChannelNames = selectedMethod.channels.map((ch: any) => ch.channel)

        // 从完整的渠道数据中过滤出对应的渠道
        const filteredChannels = store.allChannels.filter((item: any) => methodChannelNames.includes(item.channel))

        // 根据用户类型过滤渠道（代理商不显示reseller渠道）
        store.depositeChannel = store.userInfo?.isReseller ? filteredChannels.filter((item: any) => item.channel !== 'reseller') : filteredChannels
    }

    // 监听货币变化
    watch(
        () => store.wallet?.currency,
        (oldVal, newVal) => {
            if (oldVal === newVal) {
                return
            }
            getChannel()
        }
    )

    /**
     * 获取用户的渠道轮换索引
     * 使用用户ID作为key的一部分，确保不同用户有自己的轮换序列
     *
     * @returns 当前用户的渠道轮换索引
     */
    const getUserRotationIndex = () => {
        const currency = store.wallet?.currency
        const storageKey = `${CHANNEL_ROTATION_KEY}_${currency}`
        try {
            const storedValue = localStorage.getItem(storageKey)
            if (storedValue) {
                const parsedValue = JSON.parse(storedValue)
                return typeof parsedValue === 'number' ? parsedValue : 0
            }
            return 0
        } catch (e) {
            return 0
        }
    }

    /**
     * 更新用户的渠道轮换索引
     *
     * @param newIndex 新的索引值
     */
    const updateUserRotationIndex = (newIndex: number) => {
        const currency = store.wallet?.currency
        const storageKey = `${CHANNEL_ROTATION_KEY}_${currency}`
        try {
            localStorage.setItem(storageKey, JSON.stringify(newIndex))
        } catch (e) {
            // 忽略存储错误
        }
    }

    /**
     * 获取下一个可用支付渠道
     * 实现渠道轮换逻辑，每次支付自动选择不同渠道
     *
     * @returns 返回选中的渠道信息对象，如果没有可用渠道则返回null
     */
    const getNextAvailableChannel = () => {
        // 1. 获取所有可用渠道（status !== 0）
        const availableChannels = takeEffectChannel.value.filter((item: any) => item.status !== 0)
        if (availableChannels.length === 0) {
            return null // 没有可用渠道
        }

        // 2. 获取当前用户的渠道轮换索引
        let userChannelIndex = getUserRotationIndex()

        // 3. 确保索引在有效范围内（防止越界）
        userChannelIndex = userChannelIndex % availableChannels.length

        // 4. 选择渠道
        const selectedChannel = availableChannels[userChannelIndex]

        // 5. 更新索引，实现循环轮换
        updateUserRotationIndex((userChannelIndex + 1) % availableChannels.length)

        return selectedChannel
    }

    /**
     * 支付确认处理 - 核心支付逻辑
     * 用户点击确认支付按钮时的完整处理流程
     */
    const handleDirectPayment = async () => {
        // ========== 第一步: 渠道可用性检查 ==========
        const selectedChannelInfo = getNextAvailableChannel()
        if (!selectedChannelInfo) {
            showFailToast({
                message: i18n.t('wallet_deposit_tier_unavailable'),
                className: 'tip-error',
            })
            return
        }

        // 设置选中的支付渠道
        chooseChl.value = selectedChannelInfo.channel

        // ========== 第二步: 特殊渠道处理 ==========
        // 代理商渠道需要特殊处理，显示代理商专用逻辑
        if (chooseChl.value === 'reseller') {
            return false
        }

        // ========== 第三步: 支付日志记录 ==========
        // 签到页支付需要特殊的日志记录
        if (orderParams.source === 'onlinesign') {
            Log({
                event: 'onlinesign_pay',
            })
        }

        // ========== 第四步: 显示加载状态 ==========
        showLoading.value = true

        try {
            // ========== 第五步: 调用支付API创建订单 ==========
            const order = await createPaysoOrder({
                uid: store.userInfo.uid, // 用户ID
                productId: product.value.id, // 产品ID
                channel: chooseChl.value, // 选中的支付渠道
                osType: getDeviceInfo().os, // 操作系统类型
                tradeType: 'H5', // 交易类型 (H5网页支付)
                redirectUrl: location.href, // 支付完成后的回调URL
                source: product.value?.usefor || '', // 支付来源/用途
                bflowmult: !!orderParams?.bflowmult, // 是否倍数流水
            })

            // ========== 第六步: 处理API响应 ==========
            if (order.code === 200) {
                const url = order.body.guideUrl // 支付跳转URL

                // ========== 第七步: 特殊弹框场景处理 ==========
                // 对于签到页和首充页，需要存储订单ID用于后续查询
                store.depositOrder = order.body.orderId

                // ========== 第八步: 特殊支付渠道处理 ==========
                // 二维码支付渠道需要跳转到专门的引导页面
                if (chooseChl.value === 'wkqrpay') {
                    router.push({
                        path: '/payGuide',
                        query: {
                            u: encodeURIComponent(url), // 编码后的支付URL
                            coin: product.value.cny, // 充值金额
                            time: dayjs().format('YYYY-MM-DD HH:mm:ss'), // 当前时间
                        },
                    })
                    return true
                }

                // ========== 第九步: Telegram环境处理 ==========
                // 在Telegram环境中，使用Telegram的API打开链接
                if (store.isTg) {
                    if (url) {
                        webApp.openLink(url)
                    }
                    return true
                }

                // ========== 第十步: 普通浏览器环境处理 ==========
                // 在普通浏览器中，先显示确认弹框，然后打开支付页面
                if (url) {
                    showDialog({
                        title: i18n.t('Confirmation'),
                        message: i18n.t('wallet_deposit_confirm'),
                        confirmButtonText: 'Go',
                    }).then(() => {
                        // 用户确认后，在新标签页打开支付页面
                        window.open(url, '_blank')
                    })
                }

                return true
            }

            return false
        } catch (e) {
            // ========== 错误处理 ==========
            console.log(e)
            return false
        } finally {
            // ========== 最终清理 ==========
            // 无论成功失败都要隐藏加载状态
            showLoading.value = false
        }
    }

    // 初始化获取渠道
    getChannel()

    return {
        showLoading,
        handleDirectPayment,
        getChannel,
        updateChannelsByMethod,
    }
}
